/* eslint-disable react/no-multi-comp */
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useFormikContext, FieldArray } from 'formik';
import { CloseOutlined } from '@ant-design/icons';
import { Box, CustomInput } from 'atoms';
import InfoBanner from 'componentsv2/common/InfoBanner';

// ── Sub Billing Type + Per Order constants ──
export const SUB_BILLING_PER_KM = 'Per Kilometer';
export const SUB_BILLING_PER_ORDER = 'Per Order';
export const subBillingTypeOptions = [
	{ value: SUB_BILLING_PER_KM, label: 'Per Kilometer' },
	{ value: SUB_BILLING_PER_ORDER, label: 'Per Order' },
];

export const PER_ORDER_MIN_ROWS = 1;
export const PER_ORDER_MAX_ROWS = 25;
export const makeEmptyRateRow = (index) => ({ rate_id: `R${index}`, rate: '' });
export const makeDefaultRateRows = () =>
	Array.from({ length: PER_ORDER_MIN_ROWS }, (_, i) => makeEmptyRateRow(i + 1));

const isAllowedRate = (value) =>
	value === '' || value === '.' || value.endsWith('.') || Number(value) > 0;

const useRateChangeHandler = () => {
	const { setFieldValue } = useFormikContext();
	return (index) => (e) => {
		if (!isAllowedRate(e.target.value)) {
			setFieldValue(`per_order_rates.${index}.rate`, '');
		}
	};
};

// Returns a per-row rate error (shown once touched or after a submit attempt).
// Shared by both the client grid and the vendor grid.
const useRateError = () => {
	const { errors, touched, submitCount } = useFormikContext();
	return (index) => {
		const err = errors?.per_order_rates?.[index]?.rate;
		const rowTouched = touched?.per_order_rates;
		const isTouched =
			submitCount > 0 || rowTouched === true || Boolean(rowTouched?.[index]?.rate);
		return err && isTouched ? err : null;
	};
};

const RatesHeading = styled.div`
	font-size: 14px;
	font-weight: 600;
	color: #1e293b;
	margin: 4px 0 10px;
`;

const RatesTable = styled.table`
	width: 100%;
	border-collapse: collapse;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	overflow: hidden;

	thead th {
		background: #1e293b;
		color: #fff;
		font-size: 11px;
		font-weight: 600;
		letter-spacing: 0.3px;
		text-align: left;
		padding: 10px 14px;
	}
	thead th.action {
		text-align: center;
		width: 90px;
	}
	thead th.r,
	tbody td.r {
		text-align: right;
	}
	tbody td {
		padding: 8px 14px;
		border-top: 1px solid #eef1f5;
		font-size: 13px;
		color: #1f2933;
		vertical-align: middle;
	}
	tbody td.action {
		text-align: center;
	}
	tbody tr.total td {
		background: #f3f4f6;
		font-weight: 700;
		color: #15233a;
	}
`;

const RemoveRateButton = styled.button`
	border: none;
	background: transparent;
	color: #1990ff;
	cursor: pointer;
	font-size: 16px;
	line-height: 1;

	&:disabled {
		color: #cbd5e1;
		cursor: not-allowed;
	}
`;

const RatesFooter = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-top: 12px;
`;

const RatesCount = styled.span`
	font-size: 12px;
	color: #64748b;
`;

const ErrorText = styled.div`
	color: #ff4d4f;
	font-size: 12px;
	margin-top: 4px;
`;

const AddRateButton = styled.button`
	border: none;
	background: #1e3a8a;
	color: #fff;
	font-size: 13px;
	font-weight: 500;
	padding: 8px 16px;
	border-radius: 6px;
	cursor: pointer;

	&:hover {
		background: #1e40af;
	}
	&:disabled {
		background: #cbd5e1;
		cursor: not-allowed;
	}
`;

/**
 * Per Order rate grid for Variable / Fixed + Variable › Per Order.
 *
 */
const toAmount = (v) => (Number(v) || 0).toLocaleString('en-IN');
function VendorRatesGrid({ rates, isViewMode }) {
	const rateError = useRateError();
	const handleRateChange = useRateChangeHandler();
	const clientTotal = rates.reduce((sum, r) => sum + (Number(r.client_rate) || 0), 0);
	const vendorTotal = rates.reduce((sum, r) => sum + (Number(r.rate) || 0), 0);
	return (
		<>
			<InfoBanner>
				Please enter the correct Vendor rates mentioned against the already entered Client
				rates.
			</InfoBanner>
			<RatesTable>
				<thead>
					<tr>
						<th>RATE ID</th>
						<th className="r">CLIENT RATE</th>
						<th className="r">VENDOR RATE</th>
					</tr>
				</thead>
				<tbody>
					{rates.map((row, index) => (
						<tr key={row.rate_id || `rate-${index}`}>
							<td>{row.rate_id}</td>
							<td className="r">
								{row.client_rate != null ? toAmount(row.client_rate) : '-'}
							</td>
							<td>
								<CustomInput
									name={`per_order_rates.${index}.rate`}
									type="number"
									min={0}
									step="any"
									placeholder="0"
									disabled={isViewMode}
									onChange={handleRateChange(index)}
									onKeyDown={(e) => {
										if (['-', '+', 'e', 'E'].includes(e.key)) {
											e.preventDefault();
										}
									}}
								/>
								{rateError(index) && <ErrorText>{rateError(index)}</ErrorText>}
							</td>
						</tr>
					))}
					<tr className="total">
						<td>TOTAL :</td>
						<td className="r">{toAmount(clientTotal)}</td>
						<td className="r">{toAmount(vendorTotal)}</td>
					</tr>
				</tbody>
			</RatesTable>
		</>
	);
}

VendorRatesGrid.propTypes = {
	rates: PropTypes.array.isRequired,
	isViewMode: PropTypes.bool.isRequired,
};

export default function PerOrderRatesField({
	isViewMode = false,
	vendorMode = false,
	isEditMode = false,
}) {
	const { values, setFieldValue } = useFormikContext();
	const rates = values.per_order_rates || [];
	const rateError = useRateError();
	const handleRateChange = useRateChangeHandler();

	const nextRateId = () => {
		const nums = rates.map((r) => parseInt(String(r.rate_id).replace(/\D/g, ''), 10) || 0);
		return (nums.length ? Math.max(...nums) : 0) + 1;
	};
	const reindexRows = (list) =>
		isEditMode ? list : list.map((r, i) => ({ ...r, rate_id: `R${i + 1}` }));

	const handleAddRate = () =>
		setFieldValue('per_order_rates', reindexRows([...rates, makeEmptyRateRow(nextRateId())]));

	const handleRemoveRate = (index) =>
		setFieldValue('per_order_rates', reindexRows(rates.filter((_, i) => i !== index)));

	const canEditRows = !isViewMode && !vendorMode;

	return (
		<Box mb="16px">
			<RatesHeading>Order Rates</RatesHeading>
			{vendorMode ? (
				<VendorRatesGrid rates={rates} isViewMode={isViewMode} />
			) : (
				<FieldArray name="per_order_rates">
					{() => (
						<>
							<RatesTable>
								<thead>
									<tr>
										<th>RATE ID</th>
										<th>RATE (₹)</th>
										<th className="action">ACTIONS</th>
									</tr>
								</thead>
								<tbody>
									{rates.map((row, index) => (
										<tr key={row.rate_id || `rate-${index}`}>
											<td>{row.rate_id}</td>
											<td>
												<CustomInput
													name={`per_order_rates.${index}.rate`}
													type="number"
													min={0}
													step="any"
													placeholder="Enter rate"
													disabled={isViewMode}
													onChange={handleRateChange(index)}
													onKeyDown={(e) => {
														if (['-', '+', 'e', 'E'].includes(e.key)) {
															e.preventDefault();
														}
													}}
												/>
												{rateError(index) && (
													<ErrorText>{rateError(index)}</ErrorText>
												)}
											</td>
											<td className="action">
												<RemoveRateButton
													type="button"
													aria-label="Remove rate"
													disabled={
														isViewMode ||
														rates.length <= PER_ORDER_MIN_ROWS
													}
													onClick={() => handleRemoveRate(index)}
												>
													<CloseOutlined />
												</RemoveRateButton>
											</td>
										</tr>
									))}
								</tbody>
							</RatesTable>
							<RatesFooter>
								<RatesCount>
									{rates.length} of {PER_ORDER_MAX_ROWS} rates added (min{' '}
									{PER_ORDER_MIN_ROWS})
								</RatesCount>
								{canEditRows && (
									<AddRateButton
										type="button"
										disabled={rates.length >= PER_ORDER_MAX_ROWS}
										onClick={handleAddRate}
									>
										+ Add Rate
									</AddRateButton>
								)}
							</RatesFooter>
						</>
					)}
				</FieldArray>
			)}
		</Box>
	);
}

PerOrderRatesField.propTypes = {
	isViewMode: PropTypes.bool,
	vendorMode: PropTypes.bool,
	isEditMode: PropTypes.bool,
};
